import React from 'react';
import { Helmet } from 'react-helmet';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { createStore, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';

import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

import * as Sentry from '@sentry/react';
import { DataGridUtils } from '@fashionnovateam/nova-ui-toolkit';
import ToastContainer from './Components/Toast/ToastContainer';

import reducer from './reducer';
import saga from './saga';
import theme from './Theme/index';
import App from './Containers/App/App';
import { FeatureFlagsProvider } from './Lib/Contexts/FeatureFlagsContext.tsx';
import { CurrentUserProvider } from './Lib/Contexts/CurrentUserContext.tsx';
import { NavigationProvider } from './Lib/Hooks/navigation.tsx';

// create the saga middleware
const sagaMiddleware = createSagaMiddleware();
// mount it on the store
const store = createStore(reducer, applyMiddleware(sagaMiddleware));
// run sagas
sagaMiddleware.run(saga);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30000,
    },
  },
});

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [],
    environment: process.env.REACT_APP_API_ENV,
  });
}

if (process.env.REACT_APP_MUI_LICENSE) {
  DataGridUtils.setLicenseKey(process.env.REACT_APP_MUI_LICENSE);
}

export default function Main() {
  return (
    <>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <NavigationProvider>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <Provider store={store}>
                <CurrentUserProvider>
                  <FeatureFlagsProvider>
                    <Router>
                      <ToastContainer />
                      <App />
                    </Router>
                  </FeatureFlagsProvider>
                </CurrentUserProvider>
              </Provider>
            </ThemeProvider>
          </StyledEngineProvider>
          {process.env.REACT_APP_DEVTOOL !== 'disabled' && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </NavigationProvider>
      </QueryClientProvider>
    </>
  );
}
